import React, { useState } from 'react';
import api from '../api';

const ChangePasswordModal = ({ 
  isOpen, 
  onClose, 
  userId, 
  userRole = 'student', 
  isForced = false,
  onSuccess 
}) => {
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false
  });

  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setPasswordData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setError('');
      setSuccess('');
    }
  }, [isOpen]);

  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push('At least 8 characters long');
    }
    
    if (!/(?=.*[a-z])/.test(password)) {
      errors.push('At least one lowercase letter');
    }
    
    if (!/(?=.*[A-Z])/.test(password)) {
      errors.push('At least one uppercase letter');
    }
    
    if (!/(?=.*\d)/.test(password)) {
      errors.push('At least one number');
    }
    
    if (!/(?=.*[!@#$%^&*])/.test(password)) {
      errors.push('At least one special character (!@#$%^&*)');
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Check if token exists before proceeding
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication required. Please log in again.');
      return;
    }

    // Validation
    if (!passwordData.oldPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword === 'Truyon@1234') {
      setError('Please choose a different password than the default one');
      return;
    }

    if (passwordData.oldPassword === passwordData.newPassword) {
      setError('New password must be different from the current password');
      return;
    }

    // Validate new password strength
    const passwordErrors = validatePassword(passwordData.newPassword);
    if (passwordErrors.length > 0) {
      setError(`Password must have: ${passwordErrors.join(', ')}`);
      return;
    }

    try {
      setLoading(true);
      
      // Use the new universal endpoint for all users
      const endpoint = '/api/new-auth/change-password';
      
      // Prepare payload with role information
      const payload = {
        userId: userId,
        currentPassword: passwordData.oldPassword,
        oldPassword: passwordData.oldPassword,
        newPassword: passwordData.newPassword,
        userRole: userRole,
        isForcedChange: isForced // Include role to help backend handle different cases
      };

      console.log('🔐 Changing password for:', { userId, userRole });
      console.log('🔐 Using endpoint:', endpoint);
      console.log('🔐 Token present:', !!token);

      const response = await api.post(endpoint, payload);

      console.log('🔐 Password change response:', response.data);

      if (response.data.success) {
        setSuccess('Password changed successfully!');
        
        // Show success message and handle completion
        setTimeout(() => {
          if (onSuccess) {
            onSuccess(response.data.data); // Pass the response data
          }
          if (!isForced) {
            onClose();
          }
          
          // Reset form
          setPasswordData({
            oldPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
          
          // If this was a forced password change, show additional success info
          if (isForced) {
            console.log('✅ Forced password change completed successfully');
          }
        }, 2000);
      } else {
        // Handle case where response doesn't have success flag
        setError(response.data.message || 'Unexpected response from server');
      }

    } catch (err) {
      console.error('❌ Password change error:', err);
      console.error('❌ Error response:', err.response?.data);
      
      // Handle different error scenarios
      if (err.response?.status === 401) {
        setError(err.response.data?.message || 'Current password is incorrect');
      } else if (err.response?.status === 400) {
        setError(err.response.data?.message || 'Invalid password requirements');
        
        // If there are specific requirements, show them
        if (err.response.data?.requirements) {
          setError(`Password requirements: ${err.response.data.requirements.join(', ')}`);
        }
      } else if (err.response?.status === 404) {
        setError('User not found. Please contact administrator.');
      } else if (err.response?.status === 500) {
        setError('Server error. Please try again later or contact support.');
      } else if (err.code === 'NETWORK_ERROR' || !err.response) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(err.response?.data?.message || 'Failed to change password. Please try again.');
      }
      
      // For authentication errors, optionally redirect after delay
      if (err.response?.status === 401 && err.response.data?.message?.includes('Authentication')) {
        setTimeout(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          localStorage.removeItem('userRole');
          window.location.href = '/';
        }, 3000);
      }
      
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const getPasswordStrength = (password) => {
    let strength = 0;
    let color = 'bg-rose-500';
    let text = 'Weak';
    
    if (password.length >= 8) strength++;
    if (/(?=.*[a-z])/.test(password)) strength++;
    if (/(?=.*[A-Z])/.test(password)) strength++;
    if (/(?=.*\d)/.test(password)) strength++;
    if (/(?=.*[!@#$%^&*])/.test(password)) strength++;
    
    if (strength >= 4) {
      color = 'bg-emerald-500';
      text = 'Strong';
    } else if (strength >= 2) {
      color = 'bg-amber-500';
      text = 'Medium';
    }
    
    return { strength, color, text };
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      'student': 'Student',
      'faculty': 'Faculty',
      'admin': 'Admin',
      'tenant_admin': 'Organization Admin',
      'super_admin': 'Super Admin'
    };
    return roleNames[role] || 'User';
  };

  const passwordStrength = getPasswordStrength(passwordData.newPassword);

  if (!isOpen) return null;

  // Real-time criteria checks for UI visibility
  const checkRules = {
    length: passwordData.newPassword.length >= 8,
    lower: /(?=.*[a-z])/.test(passwordData.newPassword),
    upper: /(?=.*[A-Z])/.test(passwordData.newPassword),
    number: /(?=.*\d)/.test(passwordData.newPassword),
    special: /(?=.*[!@#$%^&*])/.test(passwordData.newPassword),
    notDefault: passwordData.newPassword !== 'Truyon@1234' && passwordData.newPassword !== ''
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 transition-all duration-300">
      {/* Container wrapper accommodating bottom-sheet look on mobile, centered dialog on desktop */}
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md mx-0 sm:mx-4 max-h-[92vh] sm:max-h-[85vh] overflow-y-auto shadow-2xl flex flex-col transform transition-transform animate-in slide-in-from-bottom sm:zoom-in-95 duration-200">
        
        {/* Visual Handle Drag indicator for Mobile Sheets */}
        <div className="w-12 h-1 bg-slate-200 rounded-full mx-auto my-3 sm:hidden block shrink-0" />

        {/* Header */}
        <div className="px-6 pb-4 pt-2 sm:pt-6 flex items-center justify-between border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-xl">🔒</span>
            <div>
              <h3 className="text-lg font-bold text-slate-800 tracking-tight">
                Change Password
              </h3>
              {isForced && (
                <span className="inline-flex mt-0.5 items-center bg-rose-50 text-rose-700 text-[11px] font-semibold px-2 py-0.5 rounded-md border border-rose-100">
                  Action Required
                </span>
              )}
            </div>
          </div>
          {!isForced && (
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 hover:bg-slate-50 p-1.5 rounded-lg transition-colors"
              disabled={loading}
              aria-label="Close configuration"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        
        <div className="p-6 overflow-y-auto space-y-5">
          {/* Warning for forced password change */}
          {isForced && (
            <div className="bg-amber-50/70 border border-amber-200/80 rounded-xl p-4">
              <div className="flex gap-3">
                <span className="text-amber-500 text-lg shrink-0">⚠️</span>
                <div>
                  <h4 className="text-amber-800 text-sm font-semibold">
                    Security Update Mandatory
                  </h4>
                  <p className="text-amber-700 text-xs mt-0.5 leading-relaxed">
                    You must renew your temporary credentials to protect your institutional profile features.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Current Password */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                Current Password
              </label>
              <div className="relative">
                <input
                  type={showPasswords.old ? "text" : "password"}
                  value={passwordData.oldPassword}
                  onChange={(e) => setPasswordData({...passwordData, oldPassword: e.target.value})}
                  className="w-full bg-slate-50/50 border border-slate-200 focus:bg-white rounded-xl px-3.5 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm text-slate-800 placeholder-slate-400"
                  placeholder="Type current security key"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('old')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 rounded-md"
                  disabled={loading}
                >
                  {showPasswords.old ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" /></svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  )}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPasswords.new ? "text" : "password"}
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                  className="w-full bg-slate-50/50 border border-slate-200 focus:bg-white rounded-xl px-3.5 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm text-slate-800 placeholder-slate-400"
                  placeholder="Create complex new key"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('new')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 rounded-md"
                  disabled={loading}
                >
                  {showPasswords.new ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" /></svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268-2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  )}
                </button>
              </div>
              
              {/* Dynamic Strength Progress Bar */}
              {passwordData.newPassword && (
                <div className="mt-2.5 bg-slate-50 border border-slate-100 rounded-lg p-2">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] text-slate-500 font-medium">Complexity level:</span>
                    <span className={`text-[11px] font-bold uppercase tracking-wider ${
                      passwordStrength.text === 'Strong' ? 'text-emerald-600' :
                      passwordStrength.text === 'Medium' ? 'text-amber-600' : 'text-rose-600'
                    }`}>
                      {passwordStrength.text}
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${passwordStrength.color}`}
                      style={{ width: `${(passwordStrength.strength / 5) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Confirm New Password */}
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showPasswords.confirm ? "text" : "password"}
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                  className="w-full bg-slate-50/50 border border-slate-200 focus:bg-white rounded-xl px-3.5 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm text-slate-800 placeholder-slate-400"
                  placeholder="Repeat complex key"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('confirm')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 rounded-md"
                  disabled={loading}
                >
                  {showPasswords.confirm ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" /></svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268-2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  )}
                </button>
              </div>
              
              {/* Sync check indicator */}
              {passwordData.confirmPassword && (
                <div className="mt-1.5 flex items-center justify-end">
                  {passwordData.newPassword === passwordData.confirmPassword ? (
                    <span className="flex items-center text-emerald-600 text-xs font-medium gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                      Identical security keys
                    </span>
                  ) : (
                    <span className="flex items-center text-rose-500 text-xs font-medium gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                      Keys do not match yet
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Error Notifications Container */}
            {error && (
              <div className="bg-rose-50 border border-rose-200 rounded-xl p-3.5 animate-in fade-in duration-200">
                <div className="flex gap-2.5 items-start">
                  <svg className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span className="text-rose-800 text-xs font-medium leading-relaxed">{error}</span>
                </div>
              </div>
            )}

            {/* Success Notification Banner */}
            {success && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3.5 animate-in fade-in duration-200">
                <div className="flex gap-2.5 items-start">
                  <svg className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <div>
                    <span className="text-emerald-800 text-xs font-semibold block">{success}</span>
                    {userRole && (
                      <p className="text-emerald-600 text-[11px] mt-0.5">
                        Active sync running for {getRoleDisplayName(userRole)} tier.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons Section */}
            <div className="flex flex-col-reverse sm:flex-row gap-2.5 pt-3">
              {!isForced && (
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="w-full sm:w-auto px-5 py-2.5 border border-slate-200 text-slate-700 rounded-xl text-sm font-semibold hover:bg-slate-50 active:bg-slate-100 disabled:opacity-40 transition-colors shrink-0"
                >
                  Dismiss
                </button>
              )}
              
              <button
                type="submit"
                disabled={loading || !passwordData.oldPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                className="flex-1 bg-indigo-600 text-white py-2.5 px-4 rounded-xl font-semibold hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-all text-sm shadow-md shadow-indigo-600/10"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-b-transparent" />
                    Synchronizing...
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                    Apply New Password
                  </span>
                )}
              </button>
            </div>
          </form>

          {/* Interactive Password Criteria Checker Grid */}
          <div className="bg-slate-50 border border-slate-100/80 rounded-xl p-4">
            <h4 className="text-xs font-bold text-slate-700 mb-3 tracking-wide uppercase">
              Complexity Checklist
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <div className={`flex items-center gap-2 font-medium transition-colors ${checkRules.length ? 'text-emerald-700' : 'text-slate-500'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${checkRules.length ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                Min. 8 characters
              </div>
              <div className={`flex items-center gap-2 font-medium transition-colors ${checkRules.lower ? 'text-emerald-700' : 'text-slate-500'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${checkRules.lower ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                1 Lowercase letter
              </div>
              <div className={`flex items-center gap-2 font-medium transition-colors ${checkRules.upper ? 'text-emerald-700' : 'text-slate-500'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${checkRules.upper ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                1 Uppercase letter
              </div>
              <div className={`flex items-center gap-2 font-medium transition-colors ${checkRules.number ? 'text-emerald-700' : 'text-slate-500'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${checkRules.number ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                1 Numerical integer
              </div>
              <div className={`flex items-center gap-2 font-medium transition-colors ${checkRules.special ? 'text-emerald-700' : 'text-slate-500'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${checkRules.special ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                1 Symbol (!@#$%^&*)
              </div>
              <div className={`flex items-center gap-2 font-medium transition-colors ${checkRules.notDefault ? 'text-emerald-700' : 'text-slate-500'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${checkRules.notDefault ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                Non-default string
              </div>
            </div>
          </div>

          {/* Collapsible/Clean Informational Tips */}
          <div className="bg-indigo-50/50 border border-indigo-100 rounded-xl p-3.5">
            <div className="flex gap-2.5 items-start">
              <svg className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <div className="text-indigo-900 text-xs">
                <p className="font-bold text-indigo-950 mb-1">Recommended Advice:</p>
                <ul className="space-y-1 list-disc list-inside text-indigo-800/90 pl-0.5">
                  <li>Form random short phrases into a single word string.</li>
                  <li>Avoid repurposing identical portal or email pins.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordModal;